/**
 * AIShield useManagementHealth Hook
 * Accurately reports management service and telemetry database connectivity.
 * Explicitly scoped: does NOT claim gateway, CRS inspector, or AI service health.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import type { ManagementHealthResponse } from "../types/telemetry.ts";
import { fetchHealthz } from "../services/apiClient.ts";

export interface UseManagementHealthResult {
  isHealthy: boolean;
  isConnected: boolean;
  status: string;
  database: string;
  error: Error | null;
  isLoading: boolean;
  checkHealth: () => Promise<void>;
}

export function useManagementHealth(
  options: { pollIntervalMs?: number; autoPoll?: boolean; enabled?: boolean } = {}
): UseManagementHealthResult {
  const enabled = options.enabled ?? true;
  const [data, setData] = useState<ManagementHealthResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(enabled);
  const [error, setError] = useState<Error | null>(null);

  const pollIntervalMs = options.pollIntervalMs ?? 30000;
  const autoPoll = options.autoPoll ?? true;
  const isMountedRef = useRef<boolean>(true);
  const activeSeqRef = useRef<number>(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  const checkHealth = useCallback(async () => {
    if (!enabled) return;
    const seq = ++activeSeqRef.current;
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsLoading(true);
    try {
      const res = await fetchHealthz({
        timeoutMs: 5000,
        signal: controller.signal,
      });
      if (seq === activeSeqRef.current && isMountedRef.current) {
        setData(res);
        setError(null);
      }
    } catch (err: unknown) {
      if (seq === activeSeqRef.current && isMountedRef.current) {
        if (err instanceof Error && err.name === "AbortError") {
          return;
        }
        setError(err instanceof Error ? err : new Error(String(err)));
        setData(null);
      }
    } finally {
      if (seq === activeSeqRef.current && isMountedRef.current) {
        setIsLoading(false);
        abortControllerRef.current = null;
      }
    }
  }, [enabled]);

  useEffect(() => {
    isMountedRef.current = true;
    if (!enabled) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      setIsLoading(false);
      setData(null);
      setError(null);
      return () => {
        isMountedRef.current = false;
      };
    }

    checkHealth();

    if (!autoPoll) {
      return () => {
        isMountedRef.current = false;
        activeSeqRef.current++;
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
          abortControllerRef.current = null;
        }
      };
    }

    const intervalId = setInterval(() => {
      if (typeof document === "undefined" || document.visibilityState === "visible") {
        checkHealth();
      }
    }, pollIntervalMs);

    return () => {
      isMountedRef.current = false;
      activeSeqRef.current++;
      clearInterval(intervalId);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [checkHealth, autoPoll, pollIntervalMs, enabled]);

  const isHealthy = enabled && data?.status === "ok";
  const isConnected = enabled && data?.database === "connected";

  if (!enabled) {
    return {
      isHealthy: false,
      isConnected: false,
      status: "disabled",
      database: "disconnected",
      error: null,
      isLoading: false,
      checkHealth,
    };
  }

  return {
    isHealthy,
    isConnected,
    status: data?.status ?? "unavailable",
    database: data?.database ?? "unreachable",
    error,
    isLoading,
    checkHealth,
  };
}
